/**
 * Patient-profile screen
 * ───────────────────────────────────────────────────────────
 * • Fetches the latest metadata JSON + study groups + invite
 *   status (unused / already claimed).
 * • If the invite is still open the clinician gets a
 *   “View invite” button that jumps straight to
 *   <InvitePatientPage/> so they can re-share the link / QR.
 *
 * Expects that the back-end `/patient-details` endpoint now
 * returns:
 *   {
 *     …existing fields,
 *     inviteLink: "/auth/patient-signup?id=100007",
 *     inviteUsed: false        // true once the patient signs up
 *   }
 */

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { GroupBadges } from "../components/patients/GroupBadges";
import { formatDateTime } from "../utils/dateTime";
import api from "../api/axiosInstance";

// NEW: Firestore imports
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { firebaseApp } from "../firebase/firebaseApp";
import { getFunctions, httpsCallable } from "firebase/functions";
import { renderTable } from "../components/results/renderTable";

/* camelCase → Title Case helper */
function humanize(key) {
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

/**
 * @param {{ userData: { clinicId:string }, selectedPatient: { uid:string, alias:string } }}
 */
export default function PatientsProfilePage({ userData, selectedPatient }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [details, setDetails] = useState(null);
  const [measurements, setMeasurements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // NEW: map of measurement_id -> { status: 'ok' | 'failed' | 'unknown', message?, reason? }
  const [measurementStatuses, setMeasurementStatuses] = useState({});

  const [caatEntries, setCaatEntries] = useState([]);

  /* ───── fetch on mount / patient change ───── */
  useEffect(() => {
    if (!selectedPatient || !userData?.clinicId) return;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const [detailsRes, measRes] = await Promise.all([
          api.get("/patients-api/patient-details", {
            params: {
              clinicId: userData.clinicId,
              patientId: selectedPatient.uid,
            },
          }),
          api.get("/measurements-api/measurements", {
            params: { subjectUserId: selectedPatient.uid },
          }),
        ]);

        setDetails(detailsRes.data);
        setMeasurements(measRes.data?.measurements ?? []);
      } catch (e) {
        setError(
          e.response?.data?.error || e.message || "Failed to load patient data"
        );
      } finally {
        setLoading(false); // ← only after *both* calls finish
      }
    })();
  }, [selectedPatient, userData]);

  useEffect(() => {
    if (!selectedPatient?.uid) return;

    const functions = getFunctions(firebaseApp, "europe-west2");
    const getCaat = httpsCallable(functions, "patientCaatResults");

    let cancelled = false;

    (async () => {
      try {
        const res = await getCaat({ patientUid: selectedPatient.uid });
        if (cancelled) return;

        const entries = res.data?.entries ?? [];
        setCaatEntries(entries);
      } catch (err) {
        console.error("Failed to load CAAT results", err);
        if (!cancelled) setCaatEntries([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedPatient?.uid]);

  // ─────────────────────────────────────────────
  // For each measurement, look up Firestore doc:
  //   measurements/{uid}/wha-med/{measurement_id}
  //
  // New rules:
  //  • If NO doc           → status = "failed"
  //  • If error in doc     → status = "failed"
  //  • Else (doc, no error)→ status = "ok"
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!measurements || !measurements.length || !selectedPatient?.uid) return;

    const db = getFirestore(firebaseApp);
    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        measurements.map(async (m) => {
          const id = m.measurement_id;

          if (!id) {
            // No measurement_id → treat as failed
            return [
              id ?? "_missing_id",
              {
                status: "failed",
                message: "Missing measurement_id",
                reason: null,
              },
            ];
          }

          try {
            const ref = doc(
              db,
              "measurements",
              selectedPatient.uid,
              "wha-med",
              id
            );
            const snap = await getDoc(ref);

            // 1) No Firestore doc → FAILED
            if (!snap.exists()) {
              return [
                id,
                {
                  status: "failed",
                  message: "No processing record found",
                  reason: null,
                },
              ];
            }

            const data = snap.data() || {};

            // Pull possible error fields from root and from logs{}
            const rootMessage = data.message;
            const rootReason = data.reason;
            const rootTraceback = data.traceback;

            const logs = data.logs || {};
            const logsMessage = logs.message;
            const logsReason = logs.reason;
            const logsTraceback = logs.traceback;

            const msgStrings = [rootMessage, logsMessage]
              .filter((s) => typeof s === "string")
              .map((s) => s.toLowerCase());

            const hasFailedWord = msgStrings.some((s) => s.includes("failed"));

            const hasErrorLog =
              !!rootReason ||
              !!rootTraceback ||
              !!logsReason ||
              !!logsTraceback ||
              hasFailedWord;

            // 2) Any error logs → FAILED
            if (hasErrorLog) {
              return [
                id,
                {
                  status: "failed",
                  message: rootMessage || logsMessage || null,
                  reason: rootReason || logsReason || null,
                },
              ];
            }

            // 3) Doc exists and no error logs → OK
            return [
              id,
              {
                status: "ok",
                message: null,
                reason: null,
              },
            ];
          } catch (err) {
            console.error("Failed to fetch measurement log", err);
            return [
              id,
              {
                status: "unknown",
                message: null,
                reason: null,
              },
            ];
          }
        })
      );

      if (cancelled) return;

      const map = {};
      for (const [id, status] of entries) {
        map[id] = status;
      }
      setMeasurementStatuses(map);
    })();

    return () => {
      cancelled = true;
    };
  }, [measurements, selectedPatient?.uid]);

  // console.log("loading", loading);
  // console.log(details);
  // console.log("!details && !loading", !details && !loading);

  /* ───── click handler – open invite page ───── */
  const gotoInvite = () => {
    if (!details?.inviteLink) return;
    navigate(`/patients/invite/${selectedPatient.uid}`, {
      state: {
        alias: details.alias,
        inviteLink: details.inviteLink,
      },
    });
  };

  /* ───── render ───── */
  return (
    <div className="w-full h-full p-4 flex flex-col gap-6">
      {loading ? (
        <p>Loading patient data…</p>
      ) : error ? (
        <p className="text-red-500">Error: {error}</p>
      ) : !details && !loading ? (
        <p>No data found for this patient.</p>
      ) : (
        <div className="flex flex-row h-full w-full gap-4">
          <div className="flex flex-col gap-4">
            <h1 className="title-text self-center">{selectedPatient?.alias}</h1>

            {/* study groups */}
            {details.groups?.length > 0 && (
              <section className="flex flex-col gap-2">
                <h3 className="subtitle-text">Groups</h3>
                <GroupBadges names={details.groups} />
              </section>
            )}

            {/* metadata */}
            <section className="flex flex-col gap-2">
              <h3 className="subtitle-text">Patient details</h3>
              <dl className="grid grid-cols-2 gap-2">
                {Object.entries(details)
                  .filter(
                    ([k]) =>
                      ![
                        "alias",
                        "patientId",
                        "createdAt",
                        "groups",
                        "createdBy",
                        "inviteLink",
                        "inviteUsed",
                      ].includes(k)
                  )
                  .map(([k, v]) => (
                    <>
                      {k && v ? (
                        <React.Fragment key={k}>
                          <dt>{humanize(k)}</dt>
                          <dd>{String(v)}</dd>
                        </React.Fragment>
                      ) : null}
                    </>
                  ))}
              </dl>
            </section>

            {/* invite button (only when still valid) */}
            {!details.inviteUsed && details.inviteLink && (
              <button
                type="button"
                onClick={gotoInvite}
                className="outlined-button self-start"
              >
                View/resend invite
              </button>
            )}
            <Link className="nav-title" to={`/questionnaires/eosFeedbackForm`}>
              Feedback Form
            </Link>

            {/* footer meta */}
            <footer className="flex flex-wrap gap-1 muted-text !text-xs">
              <span>ID: {details.patientId}</span>
              <span>
                Created:&nbsp;
                {details?.created_at ? details?.created_at : details?.createdAt}
              </span>
            </footer>

            <div className="flex flex-col gap-2 w-fit h-full">
              <h3 className="subtitle-text">CAAT</h3>
              {renderTable(
                "CAAT",
                caatEntries?.length || 0,
                caatEntries,
                true // showScore so the 4th column appears
              )}
            </div>
          </div>

          {/* assessments table */}
          <section className="flex flex-col gap-2 w-full h-full">
            <h3 className="subtitle-text">Assessments</h3>

            <div className="w-full h-full max-h-[80dvh] overflow-y-auto border rounded-lg">
              {/* header */}
              <div className="grid grid-cols-6 gap-1 patient-list-container font-medium">
                <span></span>
                <span className="col-span-2 text-center">Date</span>
                <span className="col-span-2 text-center">Time</span>
                {/* NEW: Status column */}
                <span className="text-center">Status</span>
              </div>

              {measurements?.length ? (
                measurements.map((m, i) => {
                  const statusObj = measurementStatuses[m.measurement_id];
                  const status = statusObj?.status || "unknown";
                  const isFailed = status === "failed";

                  return (
                    <div
                      key={m.measurement_id}
                      className="grid grid-cols-6 gap-1 patient-list-container"
                    >
                      <span>{i + 1}</span>
                      <span className="col-span-2 text-center">
                        {formatDateTime(m.date).date}
                      </span>
                      <span className="col-span-2 text-center">
                        {formatDateTime(m.time).time}
                      </span>
                      {/* NEW: status cell */}
                      <span
                        className={
                          "text-center" +
                          (isFailed ? " text-red-600 font-semibold" : "")
                        }
                        title={
                          isFailed
                            ? statusObj?.reason ||
                              statusObj?.message ||
                              "Measurement failed"
                            : ""
                        }
                      >
                        {status === "failed"
                          ? "Out of Scope*"
                          : status === "ok"
                          ? "OK"
                          : "—"}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="p-2 text-center">No assessments yet.</div>
              )}
            </div>
            <p className="muted-text">
              *Unsuitable measurement taken by user, out of scope for current
              processing algorithm version.
            </p>
          </section>
        </div>
      )}
    </div>
  );
}
